import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Autocomplete
} from '@mui/material';
import { useTranslation } from 'react-i18next';

const AddressDialog = ({ open, onClose, onSave, address }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    referring_physician: '',
    salutation: '',
    title: '',
    first_name: '',
    last_name: '',
    organization: '',
    street: '',
    postal_code: '',
    city: '',
    country: '',
    gender: ''
  });
  const [errors, setErrors] = useState({});

  const isEditMode = address !== null && address !== undefined;

  useEffect(() => {
    if (open) {
      if (isEditMode) {
        setFormData({
          referring_physician: address.referring_physician || '',
          salutation: address.salutation || '',
          title: address.title || '',
          first_name: address.first_name || '',
          last_name: address.last_name || '',
          organization: address.organization || '',
          street: address.street || '',
          postal_code: address.postal_code || '',
          city: address.city || '',
          country: address.country || 'Deutschland',
          gender: address.gender || ''
        });
      } else {
        setFormData({
          referring_physician: '',
          salutation: '',
          title: '',
          first_name: '',
          last_name: '',
          organization: '',
          street: '',
          postal_code: '',
          city: '',
          country: 'Deutschland',
          gender: ''
        });
      }
      setErrors({});
    }
  }, [open, address, isEditMode]);

  const handleChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.referring_physician.trim()) {
      newErrors.referring_physician = 'Bezeichnung ist erforderlich';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    // Prepare data for submission (remove empty strings)
    const submitData = {};
    Object.keys(formData).forEach(key => {
      if (formData[key] !== '') {
        submitData[key] = formData[key];
      }
    });

    onSave(submitData);
  };

  const salutationOptions = [
    'Herr',
    'Frau'
  ];

  const titleOptions = [
    'Dr.',
    'Prof.',
    'Prof. Dr.',
    'Dr. med.',
    'Prof. Dr. med.',
    'Dr. med. dent.',
    'Dr. rer. nat.',
    'Dipl.-Med.',
    'Dipl.-Ing.'
  ];


  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEditMode ? 'Adresse bearbeiten' : 'Neue Adresse hinzufügen'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* 1. Zeile: Bezeichnung Referring Physician */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Bezeichnung Referring Physician *"
              value={formData.referring_physician}
              onChange={handleChange('referring_physician')}
              error={!!errors.referring_physician}
              helperText={errors.referring_physician || 'Eindeutige Bezeichnung für den Physician'}
              variant="outlined"
            />
          </Box>

          {/* 2. Zeile: Geschlecht | Anrede | Titel | Vorname | Nachname */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'flex-start' }}>
            <FormControl sx={{ minWidth: 100, width: 100 }}>
              <InputLabel>Geschlecht</InputLabel>
              <Select
                value={formData.gender}
                onChange={handleChange('gender')}
                label="Geschlecht"
                size="small"
              >
                <MenuItem value="">
                  <em>Keine</em>
                </MenuItem>
                <MenuItem value="weiblich">W</MenuItem>
                <MenuItem value="männlich">M</MenuItem>
                <MenuItem value="divers">D</MenuItem>
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 80, width: 80 }}>
              <InputLabel>Anrede</InputLabel>
              <Select
                value={formData.salutation}
                onChange={handleChange('salutation')}
                label="Anrede"
                size="small"
              >
                <MenuItem value="">
                  <em>Keine</em>
                </MenuItem>
                {salutationOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Autocomplete
              sx={{ minWidth: 120, width: 120 }}
              size="small"
              options={titleOptions}
              value={formData.title}
              onChange={(event, newValue) => {
                setFormData(prev => ({
                  ...prev,
                  title: newValue || ''
                }));
              }}
              onInputChange={(event, newInputValue) => {
                setFormData(prev => ({
                  ...prev,
                  title: newInputValue || ''
                }));
              }}
              freeSolo
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Titel"
                  placeholder="Dr., Prof., etc."
                />
              )}
            />

            <TextField
              label="Vorname"
              value={formData.first_name}
              onChange={handleChange('first_name')}
              variant="outlined"
              size="small"
              sx={{ flex: 1 }}
            />

            <TextField
              label="Nachname"
              value={formData.last_name}
              onChange={handleChange('last_name')}
              variant="outlined"
              size="small"
              sx={{ flex: 1 }}
            />
          </Box>

          {/* 3. Zeile: Einrichtung/Praxis/Firma */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Einrichtung/Praxis/Firma"
              value={formData.organization}
              onChange={handleChange('organization')}
              variant="outlined"
              placeholder="z.B. Radiologische Praxis Muster, Krankenhaus XY"
            />
          </Box>

          {/* 4. Zeile: Straße und Hausnummer */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Straße und Hausnummer"
              value={formData.street}
              onChange={handleChange('street')}
              variant="outlined"
              placeholder="z.B. Musterstraße 123"
            />
          </Box>

          {/* 5. Zeile: PLZ und Ort */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <TextField
              label="PLZ"
              value={formData.postal_code}
              onChange={handleChange('postal_code')}
              variant="outlined"
              placeholder="12345"
              sx={{ minWidth: 100, width: 100 }}
            />
            <TextField
              label="Ort"
              value={formData.city}
              onChange={handleChange('city')}
              variant="outlined"
              placeholder="Musterstadt"
              sx={{ flex: 1 }}
            />
          </Box>

          {/* 6. Zeile: Land */}
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label="Land"
              value={formData.country}
              onChange={handleChange('country')}
              variant="outlined"
              placeholder="Deutschland"
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} className="button-cancel">
          Abbrechen
        </Button>
        <Button 
          onClick={handleSubmit} 
          className="button"
          disabled={!formData.referring_physician}
        >
          {isEditMode ? 'Aktualisieren' : 'Erstellen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddressDialog;
